import React from 'react';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import PropTypes from 'prop-types';
import classNames from '~/components/util/classNames';
import FormInput from './form-input';
import ReactSelect from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function FormGroup(props) {
    const { label = '', startDate } = props;

    let {
        name = kebabCase(label),
    } = props;

    let {
        index,
        placeholder = '',
        autoComplete = '',
        value = '',
        error = '',
        type = 'text',
        required,
        labelClassName = '',
        groupClassName = 'form-group',
        inputClassName = 'form-control',
        readOnly = false,
        smallText = '',
        path = '',
        onChange,
        onBlur,
        validator,
        mask = '',
        min = -99999,
        minLength = null,
        maxLength = null,
        max = '',
        options = [],
        isClearable = false,
        refProp,
        showTimeInput = false,
        timeInputLabel = 'Time:',
        showTimeSelectOnly= false,
        dateFormat = 'MM/dd/yyyy'
    } = props;
   
    if (index)
        name = `${kebabCase(label)}-${index}`;

    const hasError = !isEmpty(error);
    const hasErrorClass = hasError ? 'is-invalid' : '';
    const inputClasses = classNames('form-input', hasErrorClass, inputClassName);
    const labelClasses = classNames('form-label', labelClassName);

    let extraProps = {};
    if (min !== -99999)
        extraProps = {
            min
        };
    if (type == 'number')
        min = 0;
    
    const date_regex = /^\d{2}\/\d{2}\/\d{4}$/ ;
    const handleChangeRaw = (value, path) => {
        if(value && !value.includes('/')) {
            if(!date_regex.test(value) && value.trim().length == 8) {
                const newDate = value.substring(0,2) + '/' + value.substring(2,4)+ '/' + value.substring(4);
                if(!startDate) 
                    onChange(new Date(newDate), path);
                if(startDate && startDate != '' && (new Date(newDate) >= new Date(startDate)))
                    onChange(new Date(newDate), path);
                
            }
        }        
    };
    
    return (
        <div className={groupClassName}>
            <label htmlFor={name} className={labelClasses}>{label}{required && ' *'}</label>
            { type == 'date-picker' &&
                <DatePicker
                    className={inputClasses}
                    wrapperClassName={props.datePickerClasses}
                    readOnly={readOnly}
                    selected={value ? new Date(value): null}
                    timeInputLabel={timeInputLabel}
                    dateFormat={dateFormat}
                    popperPlacement='bottom'
                    maxDate={new Date('12/12/2100')}
                    showPopperArrow={false}
                    showTimeInput={showTimeInput}
                    showTimeSelectOnly={showTimeSelectOnly}
                    minDate={startDate ? new Date(startDate) : new Date('01/01/1900')}
                    strictParsing={dateFormat.toLowerCase() == 'mm/dd/yyyy' ? true : false}
                    onChangeRaw={(event) => {
                        if(!showTimeSelectOnly && dateFormat.toLowerCase() == 'mm/dd/yyyy')
                            handleChangeRaw(event.target.value, path);
                    }}
                    onChange={(value) => onChange(value, path)}
                    placeholderText={showTimeSelectOnly ? "hh:mm AM/PM": "MM/DD/YYYY"}
                />
            }
            { type == 'select' &&
                <ReactSelect
                    id={name}
                    name={name}
                    className={classNames(hasError && 'is-invalid')}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    value={value}
                    isDisabled={readOnly}
                    key={index}
                    onChange={value => onChange(value, path, index)}
                    options={options}
                    required={required}
                    isClearable={isClearable}
                />
            }
            { ['text', 'tel', 'decimal', 'password', 'currency', 'number'].includes(type) &&
            <FormInput 
                id={name}
                key={index}
                name={name}
                path={path}
                validator={validator}
                max={max}
                mask={mask}
                minLength={minLength}
                maxLength={maxLength}
                type={type}
                readOnly={readOnly}
                inputClassName={inputClasses}
                placeholder={placeholder}
                autoComplete={autoComplete}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                {...extraProps}
                refProp={refProp}
            />
            }
            { smallText &&
                <small className="form-text text-muted">{smallText}</small>
            }
            <div className="invalid-feedback" style={{ display: hasError ? 'block' : 'none'}}>{error}</div>
        </div>
    );
}

FormGroup.propTypes = {
    index: PropTypes.number,
    label: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    autoComplete: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string, 
        PropTypes.number,
        PropTypes.object,
    ]),
    error: PropTypes.string,
    type: PropTypes.string,
    classes: PropTypes.string,
    datePickerClasses: PropTypes.string,
    startDate: PropTypes.string,
    
    validator: PropTypes.func,
    required: PropTypes.bool,
    
    readOnly: PropTypes.bool,
    smallText: PropTypes.string,
    path: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    mask: PropTypes.string,

    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    
    labelHasHTML: PropTypes.bool,
    labelClassName: PropTypes.string,
    groupClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    boldLabel: PropTypes.bool,
    //Select props
    options: PropTypes.array,
    isClearable: PropTypes.bool,
    refProp: PropTypes.oneOfType([
        // Either a function
        PropTypes.func, 
        // Or the instance of a DOM native element (see the note about SSR)
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ]),

    //Date picker
    dateFormat: PropTypes.string,
    showTimeInput: PropTypes.bool,
    timeInputLabel: PropTypes.string,
    showTimeSelectOnly: PropTypes.bool
};

export default FormGroup;
